/**
 * OverviewPanel — The Thesis.
 *
 * Frames agentic boundary crossings as CSK seam crossings: every tool
 * call, sub-agent spawn, external read, and persistent write crosses a
 * governance boundary. A crossing not in possession of its required
 * witness tuple is structurally inexpressible — it cannot be expressed
 * in a way that type-checks.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import regimesData from "../data/regimes.json" with { type: "json" };
import witnessesData from "../data/witnesses.json" with { type: "json" };
import obligationsData from "../data/obligations.json" with { type: "json" };
import theoremsData from "../data/theorems.json" with { type: "json" };
const REGIME_TOKENS: Record<string, string> = {
  regimeTool: panelTokens.regimeTool,
  regimeSubAgent: panelTokens.regimeSubAgent,
  regimeExternal: panelTokens.regimeExternal,
  regimePersistent: panelTokens.regimePersistent,
};
export function OverviewPanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="The Thesis"
      subtitle="Every agent boundary crossing is a CSK seam crossing. The required witness tuple decides whether the crossing can even be expressed."
      accentColor={panelTokens.accent}
    >
      <PanelSurface.ThesisCard
        color={panelTokens.accent}
        glowColor="rgba(52,211,153,0.10)"
        quoteSource="RD-27 Agentic Boundary Kernel"
        quote={
          <>
            A crossing not in possession of the required witness tuple{" "}
            <strong>cannot be expressed in a way that type-checks</strong>. The
            non-compliant crossing is structurally inexpressible — not blocked,
            not detected, not scored.
          </>
        }
      >
        AI agents cross governance boundaries continuously: invoking external
        tools, spawning sub-agents, reading and writing persistent stores, and
        delegating capabilities to other principals. Each crossing is a{" "}
        <strong className="text-[var(--abk-bright)]">seam crossing</strong> in
        the CSK sense — a boundary between two distinct governance contexts. The
        Agentic Boundary Kernel translates the general CSK seam-crossing
        conditions into a deterministic, agent-centric specification: for each{" "}
        <strong className="text-[var(--abk-accent)]">seam regime</strong> it
        fixes the exact <strong>required witness tuple</strong> that must be
        exhibited for the crossing to be{" "}
        <strong className="text-[var(--abk-accent)]">
          structurally compliant
        </strong>
        . ABK adds no new CSK theorems; it pulls the existing ten back to the
        agentic seam category by construction.
      </PanelSurface.ThesisCard>

      <PanelSurface.SectionHeading>The structural claim, precisely</PanelSurface.SectionHeading>
      <PanelSurface.Callout
        title="A missing witness makes a crossing unconstructable"
        tone="info"
      >
        Holding every required witness makes the crossing constructable. Absent
        a required witness, the crossing is structurally inexpressible — the
        type system cannot form the term. There is no runtime guard that
        "rejects" the crossing; the non-compliant program never exists to be
        rejected.
      </PanelSurface.Callout>

      <PanelSurface.SectionHeading>What this measures</PanelSurface.SectionHeading>
      <PanelSurface.KpiGrid>
        <PanelSurface.KpiBox
          label="Seam regimes"
          value={String(regimesData.regimes.length)}
          color={panelTokens.accentBlue}
          subtitle="R1 · R2 · R3 · R4"
        />
        <PanelSurface.KpiBox
          label="Bridge witnesses"
          value={String(witnessesData.witnesses.length)}
          color={panelTokens.accentPurple}
          subtitle="one suite · BWS"
        />
        <PanelSurface.KpiBox
          label="Proof obligations"
          value={String(obligationsData.obligations.length)}
          color={panelTokens.accentYellow}
          subtitle="POS-A1..A7"
        />
        <PanelSurface.KpiBox
          label="Mechanized theorems"
          value={String(theoremsData.theorems.length)}
          color={panelTokens.accentTeal}
          subtitle="ABK-1..ABK-6 · all PROVED"
        />
        <PanelSurface.KpiBox
          label="Postulates"
          value="0"
          color={panelTokens.accentGreen}
          subtitle="zero · CI enforced"
        />
        <PanelSurface.KpiBox
          label="Open work"
          value="1"
          color={panelTokens.accentOrange}
          subtitle="RA-RD27-1 runtime · blocks graduation"
        />
      </PanelSurface.KpiGrid>

      <PanelSurface.SectionHeading>The four seam regimes</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.BadgeRail
          items={regimesData.regimes.map((r) => ({
            id: r.id,
            label: `${r.id} · ${r.name}`,
            color: REGIME_TOKENS[r.token] ?? panelTokens.accent,
            title: r.summary,
          }))}
        />
        <PanelSurface.Prose className="mx-1 mt-3 mb-0">
          Each regime has a distinct governance topology and a deterministic
          required-witness tuple. The Required Witness Tuple table (RD-27 §5) is
          finite and total: for any regime and obligation, the required witness
          is uniquely determined.
        </PanelSurface.Prose>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>The mapping, in one block</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Mono>{`-- Each POS-A obligation is discharged by exactly one bridge witness
POS-A1 Authority Delegation     → Permit          (PERMIT)        IMMUNE
POS-A2 Budget Monotonicity      → Budget          (RES.CONSUME)   CONTAINED
POS-A3 Fuel Accounting          → Fuel            (RES.CONSUME)   CONTAINED
POS-A4 Context Provenance       → TransportedCtx  (DATA.ORIGIN)   IMMUNE
POS-A5 Information Reachability → Declassify      (DATA.REACH)    IMMUNE
POS-A6 Freshness                → Fresh           (SEPARATE)      IMMUNE
POS-A7 Delegation Acyclicity    → Delegation      (PERMIT+COMPOSE) IMMUNE

-- ABK-1 witness-completeness : every obligation has exactly one witness`}</PanelSurface.Mono>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>How to read the rest of the demo</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.ContentList
          ariaLabel="Agentic Boundary Kernel demo reading order"
          variant="ordered"
          items={[
            {
              id: "seam-regimes",
              title: "Seam Regimes (R1–R4)",
              body: "The four canonical boundary regimes and the required witness tuple each one fixes.",
              color: panelTokens.accentBlue,
            },
            {
              id: "witness-suite",
              title: "Bridge Witness Suite",
              body: "The eight canonical witness types, each derived from one or more CSK neutralization theorems.",
              color: panelTokens.accentPurple,
            },
            {
              id: "witness-lab",
              title: "Witness Tuple Lab",
              body: "Pick a regime, hold a set of witnesses, and watch the crossing become constructable or structurally inexpressible.",
              color: panelTokens.accentYellow,
            },
            {
              id: "reachability-lab",
              title: "Reachability Slice Lab",
              body: "The DATA.REACH decision procedure on a finite authority DAG: forward ∩ backward gives the minimal slice.",
              color: panelTokens.accentCyan,
            },
            {
              id: "mechanization",
              title: "Mechanization",
              body: "ABK-1..6 with Agda anchors, the naming triad, and the honest open-status note (RA-RD27-1).",
              color: panelTokens.accentTeal,
            },
          ]}
        />
      </PanelSurface.Card>

      <PanelSurface.RepositoryLinks
        items={[
          {
            label: "RD-27 manifest",
            path: "research/settled/RD-27-AGENTIC-BOUNDARY-KERNEL.md",
          },
          {
            label: "Bridge Witness Suite",
            path: "agda/Research/AgenticBoundaryKernel/BridgeWitnessSuite.agda",
          },
          {
            label: "Seam Obligations",
            path: "agda/Research/AgenticBoundaryKernel/SeamObligations.agda",
          },
          {
            label: "Reachability Slice",
            path: "agda/Research/AgenticBoundaryKernel/ReachabilitySlice.agda",
          },
        ]}
      />
    </PanelSurface.PanelShell>
  );
}
